import { RefObject, useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './TableItem.module.css';

import {
  FaqItem,
  FeedbackItem,
  ShareLink,
  NoticeItem,
  RegistrationRequest,
} from '../../../types/settings';

import {
  ANSWER_COMPLETE,
  ANSWER_YET,
  APPROVAL,
  DELETE,
  MORE,
  REGISTERED_SHARED_LIBRARY,
  REGISTRATION,
  STUDENT_REGISTRATION_SUCCESS,
} from '../../../constants';

import { formatDate, getDiff, today } from '../../../utils/date';
import { useAppDispatch } from '../../../hooks/useRedux';
import { isOfType } from '../../../utils/typeGuard';

import {
  settingsApiSlice,
  useDeleteShareLinkNotificationMutation,
  useDeleteRegistrationRequestMutation,
  useUpdateRegistrationRequestMutation,
  useUpdateShareLinkNotificationMutation,
} from '../../../services/settings';
import { useIntersect } from '../../../hooks/useIntersect';

import {
  feedbackPageChanged,
  reqListRemoved,
  reqListUpdated,
  reqPageChanged,
  shareLinkListRemoved,
  shareLinkPageChanged,
} from '../../../features/settingsSlice';

import toast from 'react-hot-toast';
import { mainApiSlice } from '../../../services/main';
import { notificationsChanged } from '../../../features/mainSlice';

interface TItem {
  name: string;
  item: ShareLink | NoticeItem | FaqItem | FeedbackItem | RegistrationRequest;
  code?: string;
}

export default function TableItem({ name, item, code }: TItem) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();

  const [updateShareLinkNotification] =
    useUpdateShareLinkNotificationMutation();
  const [deleteShareLinknotification] =
    useDeleteShareLinkNotificationMutation();
  const [updateRegistrationRequest] = useUpdateRegistrationRequestMutation();
  const [deleteRegistrationRequest] = useDeleteRegistrationRequestMutation();
  const [getNotifications] = mainApiSlice.useLazyGetNotificationsQuery();

  const [isContentOpen, setIsContentOpen] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);

  let page = 0;

  const intersecRef = useIntersect(async (entry, observer) => {
    observer.unobserve(entry.target);
    page++;

    try {
      switch (name) {
        case 'sharedLink':
          dispatch(shareLinkPageChanged(page));
          break;
        case 'studentRegistration':
          dispatch(reqPageChanged(page));
          break;
        case 'notice':
          dispatch(settingsApiSlice.endpoints.getNotice.initiate({ page }));
          break;
        case 'faq':
          dispatch(
            settingsApiSlice.endpoints.getFaq.initiate({
              faqCd: code,
              page,
            })
          );
          break;
        case 'feedback':
          dispatch(feedbackPageChanged(page));
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('Failed to get list', error);
    }
  });

  const updateShareLink = async () => {
    const projectId = isOfType(item, 'projectId') && item.projectId;
    try {
      await updateShareLinkNotification(projectId).unwrap();
      toast(REGISTERED_SHARED_LIBRARY);
      setIsUpdated(true);
    } catch (error) {
      console.log('Failed to update shareLinkNotification', error);
    }
  };

  const updateRequest = async () => {
    const seq = isOfType(item, 'seq') && item.seq;
    const teacherAlias = isOfType(item, 'teacherAlias') && item.teacherAlias;

    try {
      await updateRegistrationRequest(seq);
      dispatch(reqListUpdated(seq));
      toast(
        STUDENT_REGISTRATION_SUCCESS.replace('{user}', teacherAlias as string)
      );
    } catch (error) {
      console.error('Failed to approve request', error);
    }
  };

  const deleteShareLink = async () => {
    const projectId = isOfType(item, 'projectId') && item.projectId;
    try {
      await deleteShareLinknotification(projectId).unwrap();
      dispatch(shareLinkListRemoved(projectId));
    } catch (error) {
      console.log('Failed to delete link', error);
    }
  };

  const deleteRequest = async () => {
    try {
      const seq = isOfType(item, 'seq') && item.seq;
      await deleteRegistrationRequest(seq);
      dispatch(reqListRemoved(seq));
    } catch (error) {
      console.error('Failed to delete request', error);
    }
  };

  const updateNotifications = async () => {
    try {
      const res = await getNotifications();
      dispatch(notificationsChanged(res.data));
    } catch (error) {
      console.error('Failed to update notifications', error);
    }
  };

  const register = () => {
    if (name === 'sharedLink') {
      updateShareLink();
    } else if (name === 'studentRegistration') {
      updateRequest();
    }
    updateNotifications();
  };

  const deleteItem = () => {
    if (name === 'sharedLink') {
      deleteShareLink();
    } else if (name === 'studentRegistration') {
      deleteRequest();
    }
    updateNotifications();
  };

  useEffect(() => {
    setIsContentOpen(false);
  }, [code]);

  return (
    <div
      className={cx('items', { active: isContentOpen })}
      ref={intersecRef as RefObject<HTMLDivElement>}
    >
      <span className={cx('sub')}>
        {('regDate' in item || 'requestDate' in item) && (
          <span className={cx('date')}>
            {formatDate(
              isOfType(item, 'regDate') ? item.regDate : item.requestDate
            )}
          </span>
        )}
        {'faqNm' in item && <span className={cx('date')}>{item.faqNm}</span>}
      </span>
      <span className={cx('sub')}>
        {(('newYn' in item && item.newYn) ||
          (name === 'notice' &&
            'regDate' in item &&
            getDiff(today, formatDate(item?.regDate)) <= 7)) && (
          <i className={cx('new_icon')}></i>
        )}
        <span className={cx('title', 'text_clip')}>
          {'newYn' in item && (isOfType(item, 'msg') ? item.msg : item.content)}
          {'title' in item && item.title}
        </span>
      </span>

      {'replyYn' in item && (
        <span className={cx('sub')}>
          <span className={cx('state', { done: item.replyYn })}>
            {item.replyYn ? ANSWER_COMPLETE : ANSWER_YET}
          </span>
        </span>
      )}

      {'content' in item && name !== 'studentRegistration' && (
        <>
          <span className={cx('sub')}>
            <button
              type="button"
              className={cx('btn', 'open_list_more', { on: isContentOpen })}
              onClick={() => setIsContentOpen(!isContentOpen)}
            >
              <i className={cx('svg_i')}></i>
              <span className={cx('blind')}>{MORE}</span>
            </button>
          </span>
          <span
            className={cx('detail', { bwh: name === 'feedback' }, name)}
            dangerouslySetInnerHTML={{ __html: item.content }}
          ></span>
        </>
      )}

      {'reply' in item && item.reply && (
        <span
          className={cx('detail')}
          dangerouslySetInnerHTML={{ __html: item.reply }}
        ></span>
      )}

      {(name === 'sharedLink' || name === 'studentRegistration') && (
        <>
          <span className={cx('sub')}>
            <button
              type="button"
              className={cx('btn', 'abtn', {
                'is--disabled': isOfType(item, 'status')
                  ? item.status === 1
                  : isUpdated ||
                    (isOfType(item, 'applyYn') && item.applyYn === 1),
              })}
              onClick={register}
            >
              {name === 'sharedLink' ? REGISTRATION : APPROVAL}
            </button>
          </span>
          <span className={cx('sub')}>
            <button
              type="button"
              className={cx('btn', 'bbtn')}
              onClick={deleteItem}
            >
              {DELETE}
            </button>
          </span>
        </>
      )}
    </div>
  );
}
